import * as React from 'react'
import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useTable } from '@/modules/drivers/context/TableContext'
import { TableHeaderColumn } from '@/modules/drivers/types'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import { VehicleModel } from '@/app/models/vehicleModel'
import GroupPicker from '@/modules/drivers/shared/DriverGroupPicker'
import SearchInput from '@/modules/drivers/shared/SearchInput'
import SearchColor from '../../../assets/icons_new/SearchColor.svg'
import useVehicleService from '@/modules/drivers/services/VehicleService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faCarSolid, faClose } from '@/lib/fa'

type VehicleGroup = {
  id: number
  name: string
  vehicleIds?: number[]
}

const getPrintoutVehiclesColumns = (
  t: (key: string) => string,
): TableHeaderColumn[] => [
  {
    title: t('printout.registration-number'),
    key: 'registrationNumber',
    type: 'string',
    sort: true,
  },
]

type VehicleRowProps = {
  vehicle: VehicleModel
  vehicleId: number
  isChecked: boolean
  isActive: boolean
  isDisabled: boolean
  toggleId: (id: number, isActive: boolean) => void
  highlightMatch: (text: string, search: string) => React.ReactNode
  search: string
}

const VehicleRow: React.FC<VehicleRowProps> = ({
  vehicle,
  vehicleId,
  isChecked,
  isActive,
  isDisabled,
  toggleId,
  highlightMatch,
  search,
}) => {
  return (
    <li
      key={vehicle.id}
      className={`tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors hover:tw-bg-primary/10 ${
        isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''
      } ${
        !isActive && isChecked
          ? 'tw-opacity-50'
          : !isActive
            ? 'tw-opacity-30'
            : ''
      }`}
      onClick={() => !isDisabled && toggleId(vehicleId, isActive)}
    >
      <div
        className="tw--m-2 tw-grid tw-place-items-center tw-p-2"
        onClick={(event) => {
          event.stopPropagation()
          if (!isDisabled) toggleId(vehicleId, isActive)
        }}
      >
        <Checkbox
          disabled={isDisabled}
          checked={isChecked}
          onChange={() => toggleId(vehicleId, isActive)}
        />
      </div>
      <FaIcon
        icon={faCarSolid}
        className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-content-base"
      />
      <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-content-base">
        {highlightMatch(`${vehicle.registrationNumber}`, search)}
      </span>
    </li>
  )
}

type Props = {
  companyId: string
  onSelectionChange: (selectedIds: string[]) => void
}

const PrintoutVehiclesTable: React.FC<Props> = ({
  companyId,
  onSelectionChange,
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const PrintoutVehiclesColumns = React.useMemo(
    () => getPrintoutVehiclesColumns(t),
    [t],
  )
  const { getVehicleList, getVehicleGroups, getVehiclesInGroup } =
    useVehicleService()

  const {
    toggleAll,
    toggleId,
    selectedGroup,
    setAllIds,
    isSelectedAny,
    isActiveGroup,
    resetAll,
  } = useTable()

  const [search, setSearch] = React.useState('')
  const [vehicleList, setVehiclesList] = React.useState<VehicleModel[]>([])
  const [groups, setGroups] = React.useState<VehicleGroup[]>([])
  const [actualGroup, setActualGroup] = React.useState<VehicleGroup | null>(
    null,
  )

  const { sortedData, setSortConfig } = useSortableData({
    data: vehicleList,
    tableColumns: PrintoutVehiclesColumns,
  })

  const filteredVehicles = React.useMemo(() => {
    let filtered = sortedData

    if (actualGroup) {
      if (actualGroup.id === -1) {
        filtered = filtered.filter((vehicle) => vehicle.isActive === true)
      } else if (actualGroup.id === -2) {
        filtered = filtered.filter((vehicle) => vehicle.isActive === false)
      } else if (actualGroup.id !== 0) {
        const groupVehicleIds = actualGroup.vehicleIds || []
        filtered = filtered.filter((vehicle) => {
          const vehicleId = parseInt(vehicle.id)
          return groupVehicleIds.includes(vehicleId)
        })
      }
    }

    if (search && search.trim() !== '') {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter((vehicle) =>
        vehicle.registrationNumber?.toLowerCase().includes(searchLower),
      )
    }

    return filtered
  }, [sortedData, search, actualGroup])

  const searchResults = filteredVehicles.length

  const visibleVehicles = React.useMemo(
    () =>
      filteredVehicles.filter((vehicle) => {
        const id = parseInt(vehicle.id)
        return !isNaN(id)
      }),
    [filteredVehicles],
  )
  const visibleVehicleIds = visibleVehicles.map((vehicle) =>
    parseInt(vehicle.id),
  )
  const selectedIds = selectedGroup?.ids ?? []
  const allVisibleChecked =
    visibleVehicleIds.length > 0 &&
    visibleVehicleIds.every((id) => selectedIds.includes(id))
  const selectedVisibleCount = visibleVehicleIds.filter((id) =>
    selectedIds.includes(id),
  ).length

  React.useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const data = await getVehicleList({ companyId: companyId })
        setVehiclesList(data)
      } catch (error) {
        console.error('Failed to fetch vehicles:', error)
      }
    }
    fetchVehicles()
  }, [companyId])
  const highlightMatch = (text: string, search: string) => {
    if (!search) return text
    const regex = new RegExp(`(${search})`, 'gi')
    const parts = text.split(regex)

    return (
      <div>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} className="tw-bg-[#FFF47B]">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </div>
    )
  }

  React.useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groupsData = await getVehicleGroups({ companyId: companyId })

        const groupsWithVehicles = await Promise.all(
          groupsData.map(async (group: any) => {
            try {
              const vehicles = await getVehiclesInGroup({
                companyId: companyId,
                groupId: group.id,
              })
              return {
                id: group.id,
                name: group.name || '',
                vehicleIds: vehicles
                  .map((v: any) => parseInt(v.id))
                  .filter((id: number) => !isNaN(id)),
              }
            } catch (error) {
              console.error(
                `Failed to fetch vehicles for group ${group.id}:`,
                error,
              )
              return {
                id: group.id,
                name: group.name || '',
                vehicleIds: [],
              }
            }
          }),
        )

        const sortedGroups = groupsWithVehicles.sort((a, b) =>
          a.name.localeCompare(b.name, 'pl', { sensitivity: 'base' }),
        )
        setGroups(sortedGroups)
      } catch (error) {
        console.error('Failed to fetch groups:', error)
      }
    }
    fetchGroups()
  }, [companyId])

  React.useEffect(() => {
    if (vehicleList.length > 0) {
      setSortConfig({ key: 'registrationNumber', direction: 'asc' })

      let vehiclesToInclude = vehicleList

      if (actualGroup) {
        if (actualGroup.id === -1) {
          vehiclesToInclude = vehicleList.filter((v) => v.isActive === true)
        } else if (actualGroup.id === -2) {
          vehiclesToInclude = vehicleList.filter((v) => v.isActive === false)
        } else if (actualGroup.id !== 0) {
          const groupVehicleIds = actualGroup.vehicleIds || []
          vehiclesToInclude = vehicleList.filter((vehicle) => {
            const vehicleId = parseInt(vehicle.id)
            return groupVehicleIds.includes(vehicleId)
          })
        }
      }

      setAllIds(
        vehiclesToInclude
          .map((vehicle) => {
            const id = parseInt(vehicle.id)
            return isNaN(id)
              ? null
              : {
                  id,
                  isActive: vehicle.isActive,
                }
          })
          .filter((item) => item !== null),
      )
    }
  }, [vehicleList, actualGroup, setAllIds, setSortConfig])

  const prevSelectionRef = React.useRef<string[]>([])
  const onSelectionChangeRef = React.useRef(onSelectionChange)

  React.useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange
  }, [onSelectionChange])

  React.useEffect(() => {
    const currentIds = selectedGroup?.ids.map((id) => id.toString()) || []
    const prevIds = prevSelectionRef.current

    const currentSet = new Set(currentIds)
    const prevSet = new Set(prevIds)

    if (
      currentIds.length !== prevIds.length ||
      !currentIds.every((id) => prevSet.has(id)) ||
      !prevIds.every((id) => currentSet.has(id))
    ) {
      prevSelectionRef.current = currentIds
      onSelectionChangeRef.current(currentIds)
    }
  }, [selectedGroup?.ids])

  const handleSelectGroup = React.useCallback(
    (group: VehicleGroup | null) => {
      setActualGroup(group)

      resetAll()
    },
    [resetAll],
  )

  const refresh = React.useCallback(async () => {
    try {
      const groupsData = await getVehicleGroups({ companyId: companyId })

      const groupsWithVehicles = await Promise.all(
        groupsData.map(async (group: any) => {
          try {
            const vehicles = await getVehiclesInGroup({
              companyId: companyId,
              groupId: group.id,
            })
            return {
              id: group.id,
              name: group.name || '',
              vehicleIds: vehicles
                .map((v: any) => parseInt(v.id))
                .filter((id: number) => !isNaN(id)),
            }
          } catch (error) {
            return {
              id: group.id,
              name: group.name || '',
              vehicleIds: [],
            }
          }
        }),
      )

      const sortedGroups = groupsWithVehicles.sort((a, b) =>
        a.name.localeCompare(b.name, 'pl', { sensitivity: 'base' }),
      )
      setGroups(sortedGroups)
    } catch (error) {
      console.error('Failed to refresh groups:', error)
    }
  }, [companyId, getVehicleGroups, getVehiclesInGroup])

  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearch('')
        setShowSearch(false)
      }
    }

    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  return (
    <section className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <div
        ref={containerRef}
        className="tw-sticky tw-top-0 tw-z-[100] tw-flex tw-items-center tw-gap-2 tw-bg-white tw-pr-4 tw-shadow-sm"
      >
        <div className="tw-flex tw-w-fit tw-items-center tw-gap-2 tw-p-3">
          <div
            className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-p-1 ${showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'}`}
            onClick={() => setShowSearch((prev) => !prev)}
          >
            <img src={SearchColor} alt="Search Icon" className="tw-w-6" />
          </div>
          <div className="tw-absolute tw-top-10">
            {showSearch && (
              <SearchInput
                search={search}
                searchResults={searchResults}
                setSearch={setSearch}
              />
            )}
          </div>
        </div>
        <div className="tw-z-[200] tw-w-full">
          <GroupPicker
            fullWidth
            companyId={companyId}
            refreshGroups={refresh}
            name={t('common.all')}
            options={groups}
            onSelect={handleSelectGroup}
            actualGroup={actualGroup}
          />
        </div>
      </div>
      <div className="tw-relative tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
        {loading ? (
          <div className="tw-grid tw-h-full tw-place-items-center">
            <LoaderIcon className="tw-w-16 tw-animate-spin" />
          </div>
        ) : visibleVehicles.length > 0 ? (
          <ul className="tw-flex tw-flex-col">
            <li className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-200 tw-bg-content-primary tw-px-3 tw-py-2">
              <Checkbox
                checked={allVisibleChecked}
                onChange={(checked) =>
                  toggleAll(checked, visibleVehicles[0]?.isActive ?? true)
                }
              />
              <span className="tw-text-xs tw-font-medium tw-text-content-base">
                {selectedVisibleCount} / {visibleVehicles.length}
              </span>

              <button
                type="button"
                disabled={selectedIds.length === 0}
                onClick={resetAll}
                title="Wyczyść zaznaczone pojazdy"
                className={`tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-rounded-md tw-px-2 tw-py-0.5 tw-text-xs tw-text-content-base tw-transition-colors hover:tw-bg-base-200 ${selectedIds.length === 0 ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
              >
                <FaIcon icon={faClose} className="tw-h-3 tw-w-3" />
                <span>Wyczyść</span>
              </button>
            </li>
            {visibleVehicles.map((vehicle) => {
              const vehicleId = parseInt(vehicle.id)
              const isChecked = selectedIds.includes(vehicleId)
              const isActive = vehicle?.isActive ?? true
              const isDisabled =
                isSelectedAny &&
                isActiveGroup !== undefined &&
                isActiveGroup !== isActive

              return (
                <VehicleRow
                  key={vehicle.id}
                  vehicle={vehicle}
                  vehicleId={vehicleId}
                  isChecked={isChecked}
                  search={search}
                  isActive={isActive}
                  highlightMatch={highlightMatch}
                  isDisabled={isDisabled}
                  toggleId={toggleId}
                />
              )
            })}
          </ul>
        ) : (
          <div className="tw-p-4 tw-text-center tw-text-xs tw-text-content-base">
            {search && search.trim() !== ''
              ? t('printout.no-search-results')
              : t('printout.no-vehicles')}
          </div>
        )}
      </div>
    </section>
  )
}

export default PrintoutVehiclesTable
